const productCardProductCache = new Map();

const PRODUCT_CARD_IMAGE_WIDTHS = [165, 360, 533, 720, 940, 1066];

const getProductCardData = async (handleProduct) => {
  if (!handleProduct) return null;

  if (!productCardProductCache.has(handleProduct)) {
    const request = fetch(
      window.Shopify.routes.root + `products/${handleProduct}.js`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Product fetch failed: ${response.status}`);
        }

        return response.json();
      })
      .catch((error) => {
        productCardProductCache.delete(handleProduct);
        throw error;
      });

    productCardProductCache.set(handleProduct, request);
  }

  return productCardProductCache.get(handleProduct);
};

const generateSrcset = (image, widths = []) => {
  const imageUrl = new URL(image.src);

  return widths
    .filter((width) => width <= image.width)
    .map((width) => {
      imageUrl.searchParams.set("width", width.toString());
      return `${imageUrl.href} ${width}w`;
    })
    .join(", ");
};

const createImageElement = (image, classes, sizes, productTitle) => {
  const previewImage = image.preview_image;
  const newImage = new Image(previewImage.width, previewImage.height);

  newImage.className = classes;
  newImage.alt = image.alt || productTitle;
  newImage.sizes = sizes;
  newImage.src = previewImage.src;
  newImage.srcset = generateSrcset(previewImage, PRODUCT_CARD_IMAGE_WIDTHS);
  newImage.loading = "lazy";

  return newImage;
};

const resetProductCardButtonState = (wrapper, variantId) => {
  wrapper.querySelector(".js-color-swatches-link")?.setAttribute(
    "href",
    wrapper.querySelector(`.js-color-swatches input[data-variant-id="${variantId}"]`)
      ?.dataset.variantLink || wrapper.querySelector(".js-color-swatches-link")?.getAttribute("href") || "#"
  );

  const addButton = wrapper.querySelector('.card__add-to-cart button[name="add"]');
  if (!addButton) return;

  addButton.setAttribute("aria-disabled", "false");
  wrapper.querySelector('.card__add-to-cart input[name="id"]').value = variantId;

  const buttonLabel = addButton.querySelector("span");
  const soldOutMessage = addButton.querySelector(".sold-out-message");

  buttonLabel?.classList.remove("hidden");
  soldOutMessage?.classList.add("hidden");
};

const updateVisibleProductCardSlider = (wrapper) => {
  const visibleSlider = Array.from(
    wrapper.querySelectorAll(".global-variant-slider")
  ).find((element) => !element.classList.contains("hidden"));

  visibleSlider
    ?.querySelector(".js-media-list, .global-variant-js-media-list")
    ?.swiper?.slideTo(0);
};

const swapProductCardImage = async (wrapper, input) => {
  const primaryImage = wrapper.querySelector(".media--first");
  if (!input.checked || !primaryImage) return;

  resetProductCardButtonState(wrapper, input.dataset.variantId);

  const currentColor = input.value;
  const secondaryImage = wrapper.querySelector(".media--second");

  try {
    const product = await getProductCardData(wrapper.dataset.product);
    if (!product?.variants) return;

    const variant = product.variants.find((item) => {
      if (!item?.featured_media) return false;

      const options =
        item.options ||
        [item.option1, item.option2, item.option3].filter(Boolean);

      return options.includes(currentColor);
    });

    if (!variant) return;

    const newPrimaryImage = createImageElement(
      variant.featured_media,
      primaryImage.className,
      primaryImage.sizes,
      product.title
    );

    if (newPrimaryImage.src === primaryImage.src) {
      updateVisibleProductCardSlider(wrapper);
      return;
    }

    let reusedSecondaryImage = false;

    if (secondaryImage) {
      const secondaryImagePathname = new URL(secondaryImage.src).pathname;
      const newPrimaryImagePathname = new URL(newPrimaryImage.src).pathname;

      if (secondaryImagePathname === newPrimaryImagePathname) {
        primaryImage.remove();
        secondaryImage.classList.remove("media--second");
        secondaryImage.classList.add("media--first");
        reusedSecondaryImage = true;
      }
    }

    if (!reusedSecondaryImage) {
      primaryImage.animate(
        { opacity: [1, 0] },
        { duration: 200, easing: "ease-in", fill: "forwards" }
      );

      setTimeout(() => {
        primaryImage.replaceWith(newPrimaryImage);
        newPrimaryImage.animate(
          { opacity: [0, 1] },
          { duration: 200, easing: "ease-in" }
        );
        secondaryImage?.remove();
      }, 200);
    }

    updateVisibleProductCardSlider(wrapper);
  } catch (error) {
    console.error("Failed to fetch product data:", error);
  }
};

const handleProductCardSwatchInteraction = (event) => {
  const input = event.target.closest(".js-color-swatches input");
  if (!input) return;

  const wrapper = input.closest(".js-color-swatches-wrapper");
  if (!wrapper || wrapper.dataset.productCardSwatchesReady !== "true") return;

  swapProductCardImage(wrapper, input);
};

const markProductCardSwatchWrappers = (root = document) => {
  root.querySelectorAll(".js-color-swatches-wrapper").forEach((wrapper) => {
    wrapper.dataset.productCardSwatchesReady = "true";
    wrapper
      .querySelectorAll(".js-color-swatches input")
      .forEach((input) => {
        input.dataset.swatchBound = "true";
      });
  });
};

const initProductCardSwatches = () => {
  if (document.body.dataset.productCardSwatchesBound === "true") {
    markProductCardSwatchWrappers();
    return;
  }

  document.body.dataset.productCardSwatchesBound = "true";
  markProductCardSwatchWrappers();
  document.addEventListener("click", handleProductCardSwatchInteraction);
};

initProductCardSwatches();

document.addEventListener("shopify:section:load", function (event) {
  markProductCardSwatchWrappers(event.target);
});
